import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { SKIP_AUTH } from '@/config/skipAuth';

const DEFAULT_INACTIVITY_MS = 20 * 60 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'] as const;

export function useInactivityLogout(inactivityMs = DEFAULT_INACTIVITY_MS) {
  const { isAuthenticated, logout } = useAuth();
  const timerRef = useRef<number | null>(null); // browser timer id (window.setTimeout returns a number)

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleLogout = useCallback(() => {
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      logout();
    }, inactivityMs);
  }, [clearTimer, inactivityMs, logout]);

  useEffect(() => {
    if (SKIP_AUTH || !isAuthenticated) {
      clearTimer();
      return;
    }

    const handleActivity = () => {
      scheduleLogout();
    };

    scheduleLogout();
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, handleActivity));

    return () => {
      clearTimer();
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, handleActivity));
    };
  }, [isAuthenticated, clearTimer, scheduleLogout]);
}
